import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Min, Scale, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, VerticalAlign } from "@pdfking/renderer-api"
import { NullQ } from "@pdfking/structs"
import { Range } from "@pdfking/tfunc"

import { LINE_STROKE, MILLIMETER_STROKE, PALETTE } from "#constants"
import type { MonthLayout } from "#internal"

import { PlotLine } from "../layout/PlotLine"
import { PlainText } from "../text/PlainText"
import { DayColor } from "./DayColor"
import { MonthName } from "./MonthName"
import { MonthWeeks } from "./MonthWeeks"
import { WeekdayFont } from "./WeekdayFont"
import { WeekdayNames } from "./WeekdayNames"

// A day's gutter off its cell's rules, in its font
const GUTTER = 0.3

/** Month `month` of `year` in `box` as the shop's templates set it: a table
 * of the weekdays from Monday over the weeks of its days, its cells as
 * `layout` has them (see MonthLayout), from the box's top left; Sundays
 * holidays (see DayColor); its names in `language`. */
export const DrawMonthGrid = ({
  box,
  language,
  layout,
  month,
  year,
}: {
  box: SpatialRectangle<Distance>
  language: string
  layout: MonthLayout
  month: number
  year: number
}): PageShape[] => {
  const weeks = MonthWeeks(year, month)
  const title = layout.title === "none" ? 0 : layout.title_scale
  // Six weeks under the weekdays and the title, where the box sets the cells
  const row = layout.height ?? Scale(box.height, 1 / (7 + title))
  const column = layout.width ?? Scale(box.width, 1 / 7)
  const font = layout.font ?? Min(Scale(row, 0.55), Scale(column, 0.42))
  const width = Scale(column, 7)
  const top = Total(box.y, box.height)
  // The foot of row `i` under the title's
  const row_y = (i: number) => Subtract(top, Total(Scale(row, title), Scale(row, i + 1)))
  const names = WeekdayNames(language).map(name =>
    layout.initials ? name.slice(0, 1).toUpperCase() : name
  )
  // A day's text in its cell as the layout aligns it, a gutter off the
  // cell's rules on an edge; the weekdays' centered
  const cell = (
    value: string,
    day: number,
    i: number,
    muted = false,
    size = font,
    horizontal = layout.horizontal_align,
    vertical = layout.vertical_align
  ) => {
    const gutter = Scale(size, GUTTER)
    return PlainText(
      value,
      Total(box.x, Scale(column, day), gutter),
      Subtract(column, Scale(gutter, 2)),
      vertical === VerticalAlign.TOP
        ? Subtract(Total(row_y(i), row), Total(gutter, size))
        : vertical === VerticalAlign.BOTTOM
          ? Total(row_y(i), gutter)
          : Total(row_y(i), Scale(Subtract(row, size), 0.5)),
      size,
      DayColor(day, muted),
      horizontal
    )
  }
  // The date in week `i` on weekday `day`, of the month before or after on
  // the weeks' ends
  const before = weeks[0]!.filter(NullQ).length
  const date = (i: number, day: number) =>
    new Date(Date.UTC(year, month - 1, 1 - before + 7 * i + day)).getUTCDate()
  // Its edges a line's stroke, the lines inside it a millimetre paper's
  const rule = (i: number, last: number) =>
    i === 0 || i === last ? LINE_STROKE : MILLIMETER_STROKE
  // The table: under the weekdays, round every week it has
  const bottom = row_y(weeks.length)
  const height = Subtract(row_y(0), bottom)
  const title_font = Scale(font, layout.title_scale)
  const title_row = Scale(row, title)
  return [
    ...(title
      ? [
          PlainText(
            (layout.title === "month"
              ? MonthName(month, language)
              : `${MonthName(month, language)} ${year}`
            ).toUpperCase(),
            box.x,
            width,
            Subtract(top, Scale(Total(title_row, title_font), 0.5)),
            title_font,
            PALETTE.secondary
          ),
        ]
      : []),
    ...names.map((name, day) =>
      cell(name, day, 0, false, WeekdayFont(font), HorizontalAlign.CENTER, VerticalAlign.CENTER)
    ),
    ...weeks.flatMap((week, i) =>
      week.flatMap((value, day) =>
        NullQ(value) && !layout.muted ? [] : [cell(`${date(i, day)}`, day, 1 + i, NullQ(value))]
      )
    ),
    ...(layout.rules
      ? [
          ...[...Range(weeks.length + 1)].map(i =>
            PlotLine(box.x, row_y(i), width, ZERO_DISTANCE, rule(i, weeks.length))
          ),
          ...[...Range(8)].map(day =>
            PlotLine(Total(box.x, Scale(column, day)), bottom, ZERO_DISTANCE, height, rule(day, 7))
          ),
        ]
      : []),
  ]
}
